import { useEffect, useRef } from 'react'

/**
 * Poll `fn` every `intervalMs`, but ONLY while the tab is visible.
 *
 * A plain setInterval keeps hitting the API from tabs nobody is looking at —
 * it fills the server access log, and for a page left open overnight it is
 * pure waste. This pauses on `visibilitychange` and fires one immediate
 * refresh when the tab comes back, so what the user sees on return is current
 * without having polled the whole time.
 *
 * Pass `enabled: false` to suspend entirely (e.g. while a modal owns the
 * screen, or once a resource reaches a terminal state).
 */
export function useVisiblePolling(
  fn: () => void | Promise<void>,
  intervalMs: number,
  enabled = true,
) {
  // Keep the latest callback without restarting the timer on every render.
  const fnRef = useRef(fn)
  useEffect(() => { fnRef.current = fn }, [fn])

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return
    let timer: ReturnType<typeof setInterval> | null = null

    const stop = () => {
      if (timer !== null) { clearInterval(timer); timer = null }
    }
    const start = () => {
      if (timer !== null) return
      timer = setInterval(() => { void fnRef.current() }, intervalMs)
    }
    const onVisibility = () => {
      if (document.hidden) { stop(); return }
      void fnRef.current()   // catch up on whatever changed while away
      start()
    }

    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs, enabled])
}

export default useVisiblePolling
